import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  DatePicker,
  Avatar,
  Tooltip,
  Empty,
} from "antd";
import {
  FaCalendarCheck,
  FaSearch,
  FaFilter,
  FaBuilding,
  FaUserClock,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaClock,
  FaSignInAlt,
  FaSignOutAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "./Projeto.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const formatTime = (str) => {
  if (!str) return "—";
  // "08:32:29" -> "08:32"
  return str.slice(0, 5);
};

// Os campos atraso/horas_extras vêm com valores negativos quebrados
// (ex: "00:-8:-35"). Tratamos como "00:00" sempre que houver algum
// componente negativo, já que representam ausência de atraso/extra real.
const formatDuration = (str) => {
  if (!str) return "00:00";
  if (str.includes("-")) return "00:00";
  const parts = str.split(":");
  if (parts.length < 2) return "00:00";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
};

const getStatusInfo = (status) => {
  const map = {
    PRESENTE: { label: "PRESENTE", color: "green", icon: <FaCheckCircle /> },
    ATRASO: { label: "ATRASO", color: "orange", icon: <FaClock /> },
    FALTA: { label: "FALTA", color: "red", icon: <FaTimesCircle /> },
    "FALTA JUSTIFICADA": {
      label: "FALTA JUSTIFICADA",
      color: "blue",
      icon: <FaExclamationCircle />,
    },
  };
  return (
    map[status] || { label: status || "—", color: "default", icon: null }
  );
};

const getEscritorioInfo = (escritorio) => {
  const map = {
    Principal: { color: "#1677ff", bg: "#e6f4ff" },
    "OK Kontact": { color: "#722ed1", bg: "#f9f0ff" },
  };
  return map[escritorio] || { color: "#8c8c8c", bg: "#f5f5f5" };
};

/* ─────────────────────────────────────────────────────────────────────────────
   ASSIDUIDADE PAGE
───────────────────────────────────────────────────────────────────────────── */
const Assiduidade = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [search, setSearch] = useState("");
  const [escritorioFilter, setEscritorioFilter] = useState("TODOS");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [userFilter, setUserFilter] = useState("TODOS");

  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  useEffect(() => {
    document.title = "Relatório de Assiduidade | Dashboard";
  }, []);

  const loadAssiduidade = useCallback(async (range) => {
    try {
      setLoading(true);
      const payload = {
        dataInicio: range[0].format("YYYY-MM-DD"),
        dataFim: range[1].format("YYYY-MM-DD"),
      };
      const response = await Api.ListAssiduidade(payload);

      // A resposta pode vir como array direto, ou como
      // { success: true, data: [...] }
      let list = [];
      if (Array.isArray(response)) {
        list = response;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        list = response.data.data;
      }

      setDados(list);
    } catch {
      toast.error("Erro ao carregar relatório de assiduidade.");
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssiduidade(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (range) => {
    if (!range || !range[0] || !range[1]) return;
    setDateRange(range);
    loadAssiduidade(range);
  };

  /* ── Achata employee -> registros em linhas de tabela ──────────────────── */
  const rows = useMemo(() => {
    const out = [];
    dados.forEach((emp) => {
      const registros = Array.isArray(emp.registros) ? emp.registros : [];
      registros.forEach((reg, idx) => {
        out.push({
          key: `${emp.employee_id}-${reg.data}-${idx}`,
          employee_id: emp.employee_id,
          nome: emp.nome,
          escritorio: emp.escritorio,
          data: reg.data,
          dia_semana: reg.dia_semana,
          status: reg.status,
          entrada: reg.entrada,
          saida: reg.saida,
          atraso: reg.atraso,
          horas_extras: reg.horas_extras,
          motivo: reg.motivo,
          obs: reg.obs,
        });
      });
    });
    return out;
  }, [dados]);

  /* ── Lista de usuários para o filtro ─────────────────────────────────── */
  const userOptions = useMemo(() => {
    const map = new Map();
    dados.forEach((emp) => {
      if (!map.has(emp.employee_id)) {
        map.set(emp.employee_id, emp.nome);
      }
    });
    return Array.from(map, ([id, nome]) => ({ id, nome }));
  }, [dados]);

  /* ── Estatísticas ────────────────────────────────────────────────────── */
  const countPresente = rows.filter((r) => r.status === "PRESENTE").length;
  const countAtraso = rows.filter((r) => r.status === "ATRASO").length;
  const countFalta = rows.filter((r) => r.status === "FALTA").length;
  const countFaltaJustificada = rows.filter(
    (r) => r.status === "FALTA JUSTIFICADA"
  ).length;

  /* ── Filtros aplicados ───────────────────────────────────────────────── */
  const filtered = rows.filter((r) => {
    const matchEscritorio =
      escritorioFilter === "TODOS" || r.escritorio === escritorioFilter;

    const matchStatus = statusFilter === "TODOS" || r.status === statusFilter;

    const matchUser = userFilter === "TODOS" || r.employee_id === userFilter;

    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.nome?.toLowerCase().includes(q) ||
      r.employee_id?.toLowerCase().includes(q);

    return matchEscritorio && matchStatus && matchUser && matchSearch;
  });

  /* ── Exportar para Excel ─────────────────────────────────────────────── */
  const handleExport = () => {
    if (filtered.length === 0) {
      toast.warning("Não há dados para exportar.");
      return;
    }

    try {
      setExporting(true);

      const exportData = filtered.map((r) => ({
        "ID Funcionário": r.employee_id,
        Nome: r.nome,
        Escritório: r.escritorio,
        Data: formatDate(r.data),
        "Dia da Semana": r.dia_semana,
        Status: r.status,
        Entrada: formatTime(r.entrada),
        Saída: formatTime(r.saida),
      
        Motivo: r.motivo || "—",
        Observação: r.obs || "—",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();

      worksheet["!cols"] = [
        { wch: 14 },
        { wch: 24 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 18 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 12 },
        { wch: 22 },
        { wch: 28 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Assiduidade");

      const inicio = dateRange[0].format("DDMMYYYY");
      const fim = dateRange[1].format("DDMMYYYY");
      XLSX.writeFile(workbook, `Relatorio_Assiduidade_${inicio}_${fim}.xlsx`);

      toast.success("Relatório exportado com sucesso!");
    } catch {
      toast.error("Erro ao exportar relatório.");
    } finally {
      setExporting(false);
    }
  };

  /* ── Colunas da tabela ───────────────────────────────────────────────── */
  const columns = [
    {
      title: "Funcionário",
      dataIndex: "nome",
      key: "nome",
      width: 240,
      render: (nome, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{
              backgroundColor: "#e6f4ff",
              color: "#1677ff",
              borderRadius: 8,
              flexShrink: 0,
            }}
            icon={<FaUserClock />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
              {nome}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.employee_id}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Escritório",
      dataIndex: "escritorio",
      key: "escritorio",
      width: 130,
      render: (escritorio) => {
        const info = getEscritorioInfo(escritorio);
        return (
          <Tag
            style={{
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              color: info.color,
              backgroundColor: info.bg,
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            icon={<FaBuilding />}
          >
            {escritorio}
          </Tag>
        );
      },
    },
    {
      title: "Data",
      key: "data",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>{formatDate(record.data)}</Text>
          <Text type="secondary" style={{ fontSize: 12, textTransform: "capitalize" }}>
            {record.dia_semana}
          </Text>
        </Space>
      ),
    },
    {
      title: "Entrada",
      dataIndex: "entrada",
      key: "entrada",
      width: 100,
      render: (entrada) => (
        <Space size={6}>
          <FaSignInAlt style={{ color: "#52c41a" }} />
          <Text style={{ fontSize: 13 }}>{formatTime(entrada)}</Text>
        </Space>
      ),
    },
    {
      title: "Saída",
      dataIndex: "saida",
      key: "saida",
      width: 100,
      render: (saida) => (
        <Space size={6}>
          <FaSignOutAlt style={{ color: "#ff4d4f" }} />
          <Text style={{ fontSize: 13 }}>{formatTime(saida)}</Text>
        </Space>
      ),
    },
  
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 170,
      render: (status, record) => {
        const { label, color, icon } = getStatusInfo(status);
        return (
          <Tooltip title={record.motivo ? `${record.motivo}${record.obs ? ` — ${record.obs}` : ""}` : undefined}>
            <Tag
              color={color}
              style={{
                borderRadius: 20,
                padding: "4px 12px",
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {icon} {label}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Layout className="projeto-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaCalendarCheck style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Relatório de Assiduidade
                  </Title>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FaFileExcel />}
                onClick={handleExport}
                loading={exporting}
                style={{
                  backgroundColor: "#fff",
                  color: "#1677ff",
                  border: "none",
                  fontWeight: 600,
                  borderRadius: 30,
                  padding: "0 24px",
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Exportar para Excel
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            {
              title: "Total de Registos",
              value: rows.length,
              color: "#1677ff",
              bgColor: "#e6f4ff",
              icon: <FaCalendarCheck style={{ fontSize: 24, color: "#1677ff" }} />,
            },
            {
              title: "Presenças",
              value: countPresente,
              color: "#52c41a",
              bgColor: "#f6ffed",
              icon: <FaCheckCircle style={{ fontSize: 24, color: "#52c41a" }} />,
            },
            {
              title: "Atrasos",
              value: countAtraso,
              color: "#faad14",
              bgColor: "#fffbe6",
              icon: <FaClock style={{ fontSize: 24, color: "#faad14" }} />,
            },
            {
              title: "Faltas / Justificadas",
              value: countFalta + countFaltaJustificada,
              color: "#ff4d4f",
              bgColor: "#fff1f0",
              icon: <FaTimesCircle style={{ fontSize: 24, color: "#ff4d4f" }} />,
            },
          ].map((stat, i) => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card
                className="stat-card"
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Statistic
                  title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>{stat.title}</span>}
                  value={stat.value}
                  valueStyle={{ fontSize: 32, fontWeight: 700, color: stat.color }}
                  prefix={
                    <div
                      style={{
                        backgroundColor: stat.bgColor,
                        borderRadius: 12,
                        padding: 10,
                        marginRight: 16,
                      }}
                    >
                      {stat.icon}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto" xs={24} md="auto" style={{ minWidth: 240 }}>
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por nome ou ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>

            <Col xs={24} md="auto">
              <RangePicker
                size="large"
                style={{ borderRadius: 30, width: "100%" }}
                format="DD/MM/YYYY"
                value={dateRange}
                onChange={handleDateChange}
                allowClear={false}
              />
            </Col>

            <Col xs={12} md="auto">
              <Select
                size="large"
                style={{ width: "100%", minWidth: 165, borderRadius: 30 }}
                value={escritorioFilter}
                onChange={setEscritorioFilter}
                suffixIcon={<FaBuilding style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos escritórios</Option>
                <Option value="Principal">Principal</Option>
                <Option value="OK Kontact">OK Kontact</Option>
              </Select>
            </Col>

            <Col xs={12} md="auto">
              <Select
                size="large"
                style={{ width: "100%", minWidth: 175, borderRadius: 30 }}
                value={statusFilter}
                onChange={setStatusFilter}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos os status</Option>
                <Option value="PRESENTE">Presente</Option>
                <Option value="ATRASO">Atraso</Option>
                <Option value="FALTA">Falta</Option>
                <Option value="FALTA JUSTIFICADA">Falta Justificada</Option>
              </Select>
            </Col>

            <Col xs={24} md="auto" flex="auto">
              <Select
                size="large"
                style={{ width: "100%", minWidth: 200, borderRadius: 30 }}
                value={userFilter}
                onChange={setUserFilter}
                showSearch
                optionFilterProp="children"
                suffixIcon={<FaUserClock style={{ color: "#1677ff" }} />}
                placeholder="Filtrar por usuário"
              >
                <Option value="TODOS">Todos os usuários</Option>
                {userOptions.map((u) => (
                  <Option key={u.id} value={u.id}>
                    {u.nome}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela de assiduidade */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="key"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} registos`,
              style: { padding: "16px 24px" },
            }}
            locale={{
              emptyText: (
                <Empty description="Nenhum registo encontrado para o período selecionado" />
              ),
            }}
            scroll={{ x: 1300 }}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default Assiduidade;